import { useEffect, useState } from 'react';

import { LinkButton } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { Icon } from '@client:common/icon';
import { Template } from '@client:common/Template';
import { Tool } from '@client:common/Tool';

import { Ranking } from '../../models/Ranking';
import { RankingCard } from '../ranking-card/RankingCard';
import { RankingTable } from '../ranking-table/RankingTable';

import './EvaluationResult.light.scss';
import './EvaluationResult.dark.scss';

interface Props {
  rankings: Ranking[];
  modelComparisonTableRowSpecifications: Array<{ path: string }>;
  template: Template;
  tools: { [toolName: string]: Tool };
}

export function EvaluationResult({ rankings, ...rest }: Props) {
  const [maxScore, setMaxScore] = useState(-Infinity);
  const [topRankings, setTopRankings] = useState<Ranking[]>([]);

  useEffect(() => {
    const newMaxScore = rankings.reduce((max, ranking) => Math.max(max, ranking.score), -Infinity);
    const newTopRankings = [] as Ranking[];

    for (const ranking of rankings) {
      if (ranking.score === newMaxScore) {
        newTopRankings.push(ranking);
      } else {
        // The runner up
        newTopRankings.push(ranking);
        break;
      }
    }
    setTopRankings(newTopRankings);
    setMaxScore(newMaxScore === 0 ? -Infinity : newMaxScore);
  }, [rankings]);

  return (
    <div className='evaluation-result'>
      <ol className='evaluation-result__ranking-list'>
        {
          topRankings.map(ranking => (
            <RankingCard
              key={ranking.score + ranking.model.name}
              ranking={ranking}
              isBest={ranking.score === maxScore}
              showReasoningList={true}>
              <LinkButton
                analyticsAction={`opened_model_in_new_tab_evaluation_results_${ranking.model.acronym}`}
                analyticsCategory='Evaluation Results'
                analyticsLabel={`Opened model in new tab from wizard evaluation results page. Model: ${ranking.model.acronym}`}
                analyticsGeneralAction="opened_model_in_new_tab_evaluation_results"
                analyticsGeneralLabel='Opened model in new tab from wizard evaluation results page. Count total of events across all new tabs from results page irrespective of model selected.'
                openInNewTab
                to={ranking.model.link}
                style='raised'
                color='primary'>
                <Ripple />
                <Icon icon='open_in_new' />
                VIEW MODEL
              </LinkButton>
            </RankingCard>
          ))
        }
      </ol>
      <RankingTable
        allRankings={rankings}
        topRankings={topRankings}
        rowSpecifications={rest.modelComparisonTableRowSpecifications}
        template={rest.template}
        tools={rest.tools} />
    </div>
  );
}
